import { Card, ETextStyleVariant, SkeletonLine, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { ProBadge } from '@/components/common/ProBadge';
import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { ESearchFilters } from '@/constants/searchFilters';
import { useRouter } from '@/hooks/useRouter';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { trackFeatureViewedEvent } from '@/services/analytics/features';
import { EFeatureName, EFeatureSource } from '@/services/analytics/features/types';
import { isCampgroundTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';

import { CampgroundPrice } from './CampgroundPrice';
import { PriceDetails } from './PriceDetails';
import { RentalInfo } from './RentalInfo';
import { RentalPrice } from './RentalPrice';
import { RentalRating } from './RentalRating';

type TListingTileContentProps = {
  rentalTile: ISearchResultTile;
  customTitle?: string;
  showProBadge?: boolean;
};

export const ListingTileContent = ({
  rentalTile,
  customTitle,
  showProBadge,
}: TListingTileContentProps) => {
  const isCampground = isCampgroundTile(rentalTile);
  const router = useRouter();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  // Get query parameters to check for dates
  const queryParams = useSelector(getQueryParams);
  const hasDates = !!queryParams[ESearchFilters.DATE_FROM] && !!queryParams[ESearchFilters.DATE_TO];

  const handleTooltipClick = (ev: React.MouseEvent<HTMLDivElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  const priceDetails =
    rentalTile.price &&
    !isCampground &&
    (rentalTile.isCampsite ? (
      <RentalPrice currency={rentalTile.currency} {...rentalTile.price} isCampsite />
    ) : (
      <PriceDetails
        showDiscount
        nights={rentalTile.price.estimatedPriceBreakdown?.duration}
        {...rentalTile.price}
      />
    ));

  const titleComponent = (
    <Text component="span" variant={ETextStyleVariant.MediumBold} className="line-clamp-2">
      {showProBadge && (
        // eslint-disable-next-line
        <span onClick={handleTooltipClick} className="align-middle">
          <ProBadge
            onMouseEnter={e => {
              const card = e.currentTarget.closest('[title]');
              if (card) {
                // Temporarily remove the title to prevent the browser's default tooltip
                card.setAttribute('data-original-title', card.getAttribute('title') ?? '');
                card.removeAttribute('title');
              }
              trackFeatureViewedEvent({
                featureName: EFeatureName.PRO_BADGE,
                rentalID: rentalTile.id,
                source: EFeatureSource.LISTING_TILE,
              });
            }}
            onMouseLeave={e => {
              const card = e.currentTarget.closest('[data-original-title]');
              if (card) {
                card.setAttribute('title', card.getAttribute('data-original-title') ?? '');
                card.removeAttribute('data-original-title');
              }
            }}
            width={24}
            height={24}
            className="inline-block mr-2"
          />
        </span>
      )}

      {(customTitle ? customTitle : rentalTile.title) ??
        (rentalTile.loading && <SkeletonLine className="bg-gray-200" />)}
    </Text>
  );

  return (
    <Card.Content className="w-full py-2">
      {titleComponent}

      <RentalInfo
        isCampground={isCampground}
        campgroundSubtitle={(isCampground && rentalTile.sitesDisplayText) || ''}
        loading={rentalTile.loading}
        isStay={isCampground ? false : rentalTile.isStay}
        title={rentalTile.title}
        {...(isEscapesSearchPage && 'escapeLocation' in rentalTile
          ? { ...rentalTile.details, location: rentalTile.escapeLocation }
          : rentalTile.details)}
        hideDetails={!!customTitle || isEscapesSearchPage || isCampground}
      />

      <div className="flex items-center justify-between my-1 gap-2">
        {!rentalTile.price && rentalTile.loading && (
          <Text component="span" variant={ETextStyleVariant.LargeBold} className="grow">
            <SkeletonLine className="bg-gray-200" />
          </Text>
        )}

        {rentalTile.price && isCampground && (
          <CampgroundPrice
            currency={rentalTile.currency}
            {...rentalTile.price}
            isEscapesSearchPage={isEscapesSearchPage}
          />
        )}

        {rentalTile.price && !isCampground && (
          <div
            className={clsx('flex flex-col', {
              'overflow-hidden': hasDates,
            })}>
            {priceDetails}
          </div>
        )}

        {!!rentalTile.details?.ratingNumber && (
          <RentalRating
            rating={rentalTile.details.ratingNumber}
            quantity={rentalTile.details.ratingQtd}
          />
        )}
      </div>
    </Card.Content>
  );
};
